import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';

import { selectStripeSubscribeSessionState } from '@client/model/selectors';
import { subscriptionSeatsApi, subscriptionSeatsKeys } from '@client/api/waterlily';

interface UseSubscriptionCheckOptions {
  advisorId: string;
}

export function useSubscriptionCheck({ advisorId }: UseSubscriptionCheckOptions) {
  const stripeSubscribeSession = useSelector(selectStripeSubscribeSessionState);

  // Fetch subscription seats data using React Query
  const { data: subscriptionSeatsData, isLoading: isFetching } = useQuery({
    queryKey: subscriptionSeatsKeys.byAdvisor(advisorId),
    queryFn: () => subscriptionSeatsApi.getByAdvisorId(advisorId),
    enabled: Boolean(advisorId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Check if advisor has active subscription by looking for an active seat
  const hasActiveSubscription = Boolean(
    subscriptionSeatsData?.seats?.some(
      seat => seat.advisorId === advisorId && seat.status === 'active',
    ),
  );

  // Check if advisor has pending invitations (only count those with status 'invited')
  const pendingInvitations = subscriptionSeatsData?.pendingSeatInvitations;
  const hasPendingInvitation = Boolean(
    pendingInvitations &&
    pendingInvitations.length > 0 &&
    pendingInvitations.some(inv => inv.status === 'invited'),
  );

  // A pilot inviter's invitation is worded as a team invitation, without subscription terms
  const hasPendingTeamInvitation = Boolean(
    pendingInvitations?.some(inv => inv.status === 'invited' && inv.isTeamInvitation),
  );

  // Don't show alerts if there's an active checkout session (user is already in checkout flow)
  const isInCheckoutFlow = Boolean(
    stripeSubscribeSession.isLoading || stripeSubscribeSession.clientSecret,
  );

  // Advisor can start a new subscription if they don't have an active one and no pending invitations
  const canStartSubscription = !hasActiveSubscription && !hasPendingInvitation;

  return {
    hasActiveSubscription,
    hasPendingInvitation,
    hasPendingTeamInvitation,
    isInCheckoutFlow,
    canStartSubscription,
    isFetching,
  };
}
